import { memo, useState, useRef, useCallback } from 'react';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableSortLabel from '@mui/material/TableSortLabel';
import { styled } from '@mui/material/styles';
import visuallyHidden from '@mui/utils/visuallyHidden';

const ResizableTableCell = styled(TableCell)(({ theme }) => ({
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: '4px',
    height: '100%',
    cursor: 'col-resize',
    backgroundColor: 'transparent',
    borderRight: `1px solid ${theme.palette.divider}`,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  '&:last-child::after': {
    display: 'none',
  },
}));

const ResizeHandle = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  right: 0,
  width: '8px',
  height: '100%',
  cursor: 'col-resize',
  backgroundColor: 'transparent',
  zIndex: 1,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:active': {
    backgroundColor: theme.palette.primary.main,
  },
}));

function ResizableTableHeader(props) {
  const {
    rowCount,
    numSelected,
    order,
    orderBy,
    cellHeader,
    onRequestSort,
    onAllSelect,
    actions,
    onColumnResize,
    columnWidths = {},
    minColumnWidth = 80,
  } = props;

  const [isResizing, setIsResizing] = useState(false);
  const [resizingColumn, setResizingColumn] = useState(null);
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(0);
  const tableRef = useRef(null);

  const createSortHandler = (property) => (e) => {
    if (!isResizing) {
      onRequestSort(e, property);
    }
  };

  const handleMouseDown = useCallback((e, columnId) => {
    e.preventDefault();
    e.stopPropagation();
    
    setIsResizing(true);
    setResizingColumn(columnId);
    resizeStartX.current = e.clientX;
    resizeStartWidth.current = columnWidths[columnId] || 150;

    const handleMouseMove = (moveEvent) => {
      const diff = moveEvent.clientX - resizeStartX.current;
      const newWidth = Math.max(minColumnWidth, resizeStartWidth.current + diff);
      
      if (onColumnResize) {
        onColumnResize(columnId, newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      setResizingColumn(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [columnWidths, onColumnResize, minColumnWidth]);

  return (
    <TableHead ref={tableRef}>
      <TableRow>
        <TableCell padding="checkbox" style={{ width: '48px' }}>
          <Checkbox
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onAllSelect}
          />
        </TableCell>
        {cellHeader.map((cell, index) => {
          const isLastColumn = index === cellHeader.length - 1;
          const cellWidth = columnWidths[cell.id] || cell.width || 150;
          
          return (
            <ResizableTableCell
              key={cell.id}
              align={cell.align}
              sortDirection={orderBy === cell.id ? order : false}
              style={{ 
                width: cellWidth,
                minWidth: cell.minWidth || minColumnWidth,
                position: 'relative',
              }}
            >
              <TableSortLabel
                hideSortIcon
                active={orderBy === cell.id}
                direction={orderBy === cell.id ? order : 'asc'}
                onClick={createSortHandler(cell.id)}
                style={{ width: '100%' }}
              >
                <Typography variant="subtitle2" noWrap>
                  {cell.label}
                </Typography>
                {orderBy === cell.id && (
                  <Box sx={{ ...visuallyHidden }}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                )}
              </TableSortLabel>
              
              {!isLastColumn && (
                <ResizeHandle
                  onMouseDown={(e) => handleMouseDown(e, cell.id)}
                  style={{
                    backgroundColor: resizingColumn === cell.id ? 'rgba(25, 118, 210, 0.3)' : 'transparent',
                  }}
                />
              )}
            </ResizableTableCell>
          );
        })}
        {actions && <TableCell style={{ width: '120px' }}>Actions</TableCell>}
      </TableRow>
    </TableHead>
  );
}

ResizableTableHeader.defaultProps = {
  onAllSelect: () => {},
  onRequestSort: () => {},
  onColumnResize: () => {},
  cellHeader: [],
  order: 'asc',
  numSelected: 0,
  orderBy: '',
  actions: false,
  columnWidths: {},
  minColumnWidth: 80,
};

export default memo(ResizableTableHeader);
